// src/components/Dashboard/MarketOverviewTab.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import FirebaseNewsCard from '../MarketOverview/FirebaseNewsCard';
import NseSectorCard from '../MarketOverview/NseSectorCard';
import { Container, Alert, Row, Col, Card, Button } from 'react-bootstrap';
import './Dashboard.css';
import { TickerTape, MarketOverview, StockHeatmap } from "react-ts-tradingview-widgets";
import { useUserRecommendationAccess } from '../../hooks/useUserRecommendationAccess';
import useNseSectors from '../../hooks/useNseSectors';


const MarketOverviewTab = () => {
  const { user } = useContext(AuthContext);
  const hasPremiumAccess = user?.role === 'premium' || user?.role === 'admin'; // Added safe navigation for user
  const [isMounted, setIsMounted] = useState(false); // State for mount status
  // WCAG 2.2.2 (audit M8): auto-scrolling ticker must be hideable by the user
  const [showTicker, setShowTicker] = useState(true);

  // Fetch Firestore recommendations (same data source as Stock Recommendation Report)
  const { recommendations: firestoreRecommendations, loading: firestoreLoading, error: firestoreError } = useUserRecommendationAccess();

  // NSE sector data — lifted here so the entire card Col can be hidden on error/no data
  const { data: sectors, loading: sectorsLoading, error: sectorsError, stale: sectorsStale, fetchedAt: sectorsFetchedAt } = useNseSectors();
  // State specifically for the formatted tabs needed by MarketOverview widget
  const [marketTabs, setMarketTabs] = useState([]); // Initialize as empty array



  // This useEffect processes Firestore recommendations to create the marketTabs structure
  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (firestoreRecommendations && firestoreRecommendations.length > 0) {
      try {
        // Extract unique stockNames from Firestore recommendations, filter out any null/undefined/empty stockNames
        const uniqueStockNames = [...new Set(
          firestoreRecommendations
            .map(rec => rec?.stockName)
            .filter(Boolean) // Filter out null, undefined, or empty strings
        )];

        if (uniqueStockNames.length > 0) {
          // Format the symbols into the structure required by MarketOverview tabs
          // Add BSE: prefix for TradingView (consistent with TradingViewWidget.js pattern)
          const formattedWatchlistTab = {
            title: "Recommended Watchlist", // Title for the tab
            symbols: uniqueStockNames.map(stockName => ({
              s: `BSE:${stockName}`, // TradingView symbol with BSE prefix (e.g., "BSE:RELIANCE")
              d: stockName  // Display name (stock symbol without prefix)
            })),
            originalTitle: "Recommended Watchlist" // Keep originalTitle consistent
          };
          // Set the state with the formatted tab data
          setMarketTabs([formattedWatchlistTab]);
        } else {
          setMarketTabs([]); // Set empty if no valid stock names found
        }

      } catch (err) {
        console.error('Error processing Firestore recommendations for market tabs:', err);
        setMarketTabs([]); // Reset on error
      }
    } else {
      setMarketTabs([]); // Reset if firestoreRecommendations is not available or empty
    }
  }, [firestoreRecommendations]); // Rerun when firestoreRecommendations changes

  // Effect to set mount status after initial render
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false); // Attempt to prevent rendering during unmount phase
  };
  }, []);



  return (
    <Container className="pt-3">
      <h1 className="visually-hidden">Market Overview</h1>
       <Row>
        <Col className="text-start mb-2">
           <a href="/legal#news-policy" target="_blank" rel="noopener noreferrer" className="news-policy-link">
              Subject to our News Policy
            </a>
        </Col>
      </Row>
      {/* Ticker Tape at the top */}
      <Row>
        <Col md={12}>
          <div className="text-end mt-2">
            <Button
              variant="link"
              size="sm"
              className="p-0 news-policy-link"
              aria-expanded={showTicker}
              onClick={() => setShowTicker(!showTicker)}
            >
              {showTicker ? 'Hide scrolling ticker' : 'Show scrolling ticker'}
            </Button>
          </div>
          {isMounted && showTicker && (
            <TickerTape
              symbols={[
                {
                  "description": "SENSEX",
                  "proName": "BSE:SENSEX"
                },
                {
                  "description": "S&P 500",
                  "proName": "FOREXCOM:SPXUSD"
                },
                {
                  "description": "DJI",
                  "proName": "FOREXCOM:DJI"
                },
                {
                  "description": "HANGSANG",
                  "proName": "IG:HANGSENG"
                },
                {
                  "description": "NIKKIE1",
                  "proName": "VANTAGE:NIKKEI225"
                },
                {
                  "description": "XJO",
                  "proName": "FOREXCOM:AUS200"
                },
                {
                  "description": "TSEC",
                  "proName": "INDEX:TAIEX"
                }
              ]}
              colorTheme="light"
              isTransparent={false}
              displayMode="adaptive"
              locale="en"
              showSymbolLogo={true}
            />
          )}
        </Col>
      </Row>

      {/* Market Heatmap - Full width on mobile, 8 columns on desktop */}
      <Row className="g-4 mb-4">
        <Col xs={12} md={12}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <Card.Title as="h2" className="dashboard-card-title">Market Heatmap</Card.Title>
              {isMounted && (
                <StockHeatmap
                  colorTheme="light"
                  exchanges={["BSE"]}
                  isTransparent={false}
                  locale="en"
                  width="100%"
                  height={500}
                  showChart={true}
                  showLegend={true}
                  showScale={true}
                  showTooltip={true}
                  symbolUrl=""
                  dataSource="SENSEX"
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sector Wise Change and Recommended Watchlist */}
      <Row className="g-4 mb-4">
        {/* Sector Wise Change — hidden when data unavailable */}
        {(sectorsLoading || (!sectorsError && sectors && sectors.length > 0)) && (
          <Col xs={12} md={6}>
            <Card className="dashboard-card h-100">
              <Card.Body>
                <Card.Title as="h2" className="dashboard-card-title">Sector Wise Change</Card.Title>
                <NseSectorCard sectors={sectors} loading={sectorsLoading} error={sectorsError} stale={sectorsStale} fetchedAt={sectorsFetchedAt} />
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Recommended Watchlist - Full width on mobile, 6 columns on desktop */}
        <Col xs={12} md={6}>
          <Card className="dashboard-card h-100">
            <Card.Body>
              <Card.Title as="h2" className="dashboard-card-title">Recommended Watchlist</Card.Title>
              {isMounted && hasPremiumAccess && marketTabs.length > 0 ? (
                <MarketOverview
                  tabs={marketTabs}
                  height={400}
                  width="100%"
                  colorTheme="light"
                  dateRange="12M"
                  showChart={true}
                  locale="en"
                  largeChartUrl=""
                  isTransparent={false}
                  showSymbolLogo={true}
                  showFloatingTooltip={false}
                  plotLineColorGrowing="rgba(41, 98, 255, 1)"
                  plotLineColorFalling="rgba(255, 73, 73, 1)"
                  gridLineColor="rgba(240, 243, 250, 0)"
                  scaleFontColor="rgba(106, 109, 120, 1)"
                  belowLineFillColorGrowing="rgba(41, 98, 255, 0.12)"
                  belowLineFillColorFalling="rgba(255, 73, 73, 0.12)"
                  belowLineFillColorFallingGrowing="rgba(33, 150, 243, 0.12)"
                  symbolActiveColor="rgba(41, 98, 255, 0.12)"
                  tabsLayout="adaptive"
                />
              ) : (
                <Alert variant="info" className="mt-3 premium-locked-alert">
                  {isMounted && firestoreLoading ? "Loading watchlist..." :
                    !hasPremiumAccess ? "🔐 Access restricted to subscribers. Please upgrade your plan to view this content." :
                      firestoreError ? `Error loading recommendations: ${firestoreError}` :
                        isMounted ? "No watchlist data available. You don't have access to any recommendations yet." : "Loading..."}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Daily News at the bottom */}
      <Row className="g-4 mb-4">
        <Col xs={12}>
          <Card className="dashboard-card">
            <Card.Body>
              <Card.Title as="h2" className="dashboard-card-title">Daily News</Card.Title>
              <FirebaseNewsCard />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MarketOverviewTab;